/* eslint-disable react-hooks/exhaustive-deps */
import { useOutletContext } from 'react-router-dom'
import { useState, useCallback, useMemo } from 'react'
import { Grid } from '@design-systems/canvas-ui'
import {
    GeneralizedComponent,
    GeneralizedComponentPayloadType,
} from 'components/generalized-component/generalized-component'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { formattedTimeStamp } from '../../helpers'
import { isGroup } from 'utils/location'
import {
    buildDefaultGenCompPayload,
    makeSortFn,
    pinAndSortRows,
} from './ship-rgd-helpers'

export const StoreFulfillmentShipRGD: React.FC = () => {
    const { t, currentLocation } = useEssentials()
    const { headings, toggles, titles } = TRANSLATION_KEYS.storeFulfillment
    const { data } = useOutletContext<{ data: any }>()
    const [sortDirection, setSortDirection] = useState<'asc' | 'dsc' | 'none'>(
        'none',
    )
    const [sortColumnIndex, setSortColumnIndex] = useState<number>(1)

    const fulfillmentTitle = useMemo(() => {
        const portraitData =
            data?.[0]?.ship_from_store?.ship_from_store_portrait
        const timeStamp = portraitData?.sfs_time_utc ?? ''
        return `${t(titles.todayAsOf)} ${formattedTimeStamp(timeStamp)}`
    }, [data, t])

    const defaultPayload = useMemo(() => {
        if (!data?.length) return {} as GeneralizedComponentPayloadType
        return buildDefaultGenCompPayload(data, {
            headings,
            currentLocation,
            t,
            toggles,
        })
    }, [data, currentLocation, t])

    const sortFn = useMemo(
        () =>
            makeSortFn(
                sortDirection,
                sortDirection === 'none' ? 0 : sortColumnIndex,
            ),
        [sortDirection, sortColumnIndex],
    )

    const payload = useMemo(() => {
        const tabKey = isGroup(currentLocation) ? 'group' : 'nonGroup'

        return (
            Object.keys(defaultPayload) as Array<keyof typeof defaultPayload>
        ).reduce(
            (acc, key) => {
                const cards = defaultPayload[key]
                const [first, ...rest] = cards || []
                const table = first.data[tabKey]
                const rows = pinAndSortRows([...table.rows], sortFn)
                acc[key] = [
                    {
                        ...first,
                        data: { ...first.data, [tabKey]: { ...table, rows } },
                    },
                    ...rest,
                ]
                return acc
            },
            {} as typeof defaultPayload,
        )
    }, [defaultPayload, currentLocation, sortDirection, sortColumnIndex])

    const onSort = useCallback(
        (
            direction: 'asc' | 'dsc' | undefined,
            field: string,
            columnIndex?: number,
        ) => {
            setSortDirection(direction ?? 'none')
            if (columnIndex !== undefined) {
                setSortColumnIndex(columnIndex)
            }
        },
        [],
    )

    // Define which columns should be sortable
    const columnSortSettings = useMemo(() => {
        return Array.from({ length: 8 }, (_, index) => ({
            [index]: {
                sortable: true,
                onSort: (direction: 'asc' | 'dsc' | undefined, field: string) =>
                    onSort(direction, field, index),
            },
        })).reduce((acc, curr) => ({ ...acc, ...curr }), {})
    }, [onSort])

    return (
        <Grid.Container>
            <Grid.Item
                className="hc8-pa-3x"
                size={{
                    xs: 12,
                }}
            >
                <GeneralizedComponent
                    payload={payload}
                    title={fulfillmentTitle}
                    scrollableWidth
                    widths={[90, 90, 90, 100, 90, 80, 80, 80]}
                    columnSortSettingsByIndex={columnSortSettings}
                    columnSize={12}
                />
            </Grid.Item>
        </Grid.Container>
    )
}
